// F-084 — Contrats fournisseurs : échéances et préavis sourcés, rappels, factures inhabituelles à confirmer.
import { useState } from "react";
import { del, post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Checkbox, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Stat, Table, Textarea, fieldError } from "@core/ui";
import { Amount, BuildingSelect, DeadlineBadge, DocLink, PropertySelect, required, useAction, useCatalog, useRefs } from "./shared";

type Deadlines = { term_end: string | null; notice_deadline: string | null; state: string; days_to_notice: number | null };
type Contract = {
  id: string;
  kind: string;
  kind_label: string;
  supplier: string;
  label: string;
  reference: string;
  property_id: string | null;
  property_label: string | null;
  building_id: string | null;
  building_label: string | null;
  start_on: string;
  end_on: string | null;
  tacit_renewal: boolean;
  renewal_months: number;
  notice_days: number;
  annual_cost_cents: number | null;
  document_id: string | null;
  document_filename: string | null;
  clause_source: string;
  dates_confirmed: boolean;
  status: string;
  notes: string;
  deadlines: Deadlines;
  warnings: string[];
  year_spent_cents: number;
  operations?: { id: string; label: string; date: string; amount_cents: number; document_id: string | null; unusual: boolean }[];
};

const STATUS: Record<string, string> = { actif: "Actif", resilie: "Résilié", termine: "Terminé" };

export function ContractsPage() {
  const org = useOrg();
  const [status, setStatus] = useState("actif");
  const q = useGet<{ items: Contract[]; annual_cost_cents: number }>(org.api(`/finance/supplier-contracts${status ? `?status=${status}` : ""}`));
  const [editing, setEditing] = useState<Contract | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const canEdit = org.can("finance");
  const items = q.data?.items ?? [];
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Contrats fournisseurs"
        intro="Assurances, entretien, énergie : échéances, préavis de résiliation et coûts. Bailivo prépare des rappels ; il ne résilie ni ne reconduit rien à votre place."
        actions={canEdit ? <Button onClick={() => setEditing("new")}>Ajouter un contrat</Button> : null}
      />
      <div className="finances-filtres">
        <Select label="Statut" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: "actif", label: "Actifs" }, { value: "resilie", label: "Résiliés" }, { value: "termine", label: "Terminés" }, { value: "", label: "Tous" }]} />
      </div>
      {q.data ? (
        <div className="stats">
          <Stat label="Contrats affichés" value={items.length} />
          <Stat label="Coût annuel des contrats actifs" value={<Amount cents={q.data.annual_cost_cents} />} />
          <Stat label="Préavis dans moins de 60 jours" value={items.filter((c) => c.deadlines.state === "a_renouveler").length} />
        </div>
      ) : null}
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 ? <Empty title="Aucun contrat.">Ajoutez votre assurance propriétaire non occupant ou vos contrats d’entretien pour être prévenu avant la date limite de résiliation.</Empty> : null}
      {items.length ? (
        <Table caption="Contrats fournisseurs">
          <thead>
            <tr>
              <th>Fournisseur</th>
              <th>Concerne</th>
              <th>Échéance</th>
              <th>Résiliation possible jusqu’au</th>
              <th>État</th>
              <th className="right">Coût annuel</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.supplier}
                  <span className="finances-sous">{c.kind_label}{c.reference ? ` · ${c.reference}` : ""}</span>
                </td>
                <td>{c.property_label ?? c.building_label ?? "Tout le patrimoine"}</td>
                <td>{date(c.deadlines.term_end)}</td>
                <td>
                  {date(c.deadlines.notice_deadline)}
                  <span className="finances-sous">{c.clause_source ? `Source : ${c.clause_source}` : "Clause non indiquée"}</span>
                </td>
                <td>
                  <div className="row">
                    {c.status === "actif" ? <DeadlineBadge state={c.deadlines.state} /> : <Badge>{STATUS[c.status]}</Badge>}
                    {c.warnings.length ? <Badge tone="attention">À vérifier</Badge> : null}
                  </div>
                </td>
                <td className="right"><Amount cents={c.annual_cost_cents} /></td>
                <td>
                  <Button variant="discret" small onClick={() => setOpen(c.id)}>Détails</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      {editing ? <ContractModal contract={editing === "new" ? null : editing} onClose={() => setEditing(null)} /> : null}
      {open ? <ContractDetail id={open} onClose={() => setOpen(null)} onEdit={(c) => { setOpen(null); setEditing(c); }} /> : null}
    </div>
  );
}

function ContractDetail({ id, onClose, onEdit }: { id: string; onClose: () => void; onEdit: (c: Contract) => void }) {
  const org = useOrg();
  const q = useGet<Contract>(org.api(`/finance/supplier-contracts/${id}`));
  const a = useAction();
  const c = q.data;
  const canEdit = org.can("finance");
  const refresh = [org.api("/finance")];
  return (
    <Modal open onClose={onClose} title={c ? `${c.kind_label} — ${c.supplier}` : "Contrat"}>
      <div className="stack">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error ?? a.error} />
        {c ? (
          <>
            {c.warnings.map((w) => (
              <Notice key={w} tone="attention">{w}</Notice>
            ))}
            <KeyValues
              items={[
                ["Période en cours", `du ${date(c.start_on)} ${c.end_on ? `au ${date(c.end_on)}` : "sans date de fin"}`],
                ["Reconduction", c.tacit_renewal ? `Tacite, par période de ${c.renewal_months} mois` : "Non"],
                ["Préavis de résiliation", `${c.notice_days} jours`],
                ["Échéance suivie", date(c.deadlines.term_end)],
                ["Date limite de résiliation", <>{date(c.deadlines.notice_deadline)} {c.status === "actif" ? <DeadlineBadge state={c.deadlines.state} /> : null}</>],
                ["Source de l’échéance", c.clause_source || "Non indiquée"],
                ["Contrat joint", c.document_id ? <DocLink id={c.document_id} name={c.document_filename} /> : "Aucun"],
                ["Coût annuel indiqué", <Amount cents={c.annual_cost_cents} />],
                ["Payé cette année", <Amount cents={c.year_spent_cents} />],
              ]}
            />
            <h3>Factures rattachées</h3>
            {(c.operations ?? []).length === 0 ? <p className="muted small">Aucune dépense rattachée. Choisissez ce contrat lors de la saisie d’une dépense.</p> : null}
            <ul className="list-plain">
              {(c.operations ?? []).map((o) => (
                <li key={o.id} className="row-between">
                  <span>
                    {date(o.date)} — {o.label} {o.document_id ? <DocLink id={o.document_id} name="(pièce)" /> : null}
                  </span>
                  <span className="row">
                    <Amount cents={o.amount_cents} />
                    {o.unusual ? <Badge tone="attention">Montant inhabituel, à confirmer</Badge> : null}
                  </span>
                </li>
              ))}
            </ul>
            <p className="small muted">Un montant est signalé lorsqu’il dépasse de plus de 25 % la médiane des factures précédentes : c’est un point à vérifier, pas une conclusion.</p>
            {canEdit ? (
              <div className="finances-actions">
                <Button variant="secondaire" small onClick={() => onEdit(c)}>Modifier</Button>
                {c.status === "actif" ? (
                  <>
                    <Button variant="secondaire" small busy={a.busy} onClick={() => a.run(() => post(org.api(`/finance/supplier-contracts/${c.id}/status`), { status: "resilie" }), { success: "Contrat marqué résilié ; le rappel est annulé.", refresh })}>
                      J’ai résilié ce contrat
                    </Button>
                    <Button variant="secondaire" small busy={a.busy} onClick={() => a.run(() => post(org.api(`/finance/supplier-contracts/${c.id}/status`), { status: "termine" }), { success: "Contrat marqué terminé.", refresh })}>
                      Contrat terminé
                    </Button>
                  </>
                ) : (
                  <Button variant="secondaire" small busy={a.busy} onClick={() => a.run(() => post(org.api(`/finance/supplier-contracts/${c.id}/status`), { status: "actif" }), { success: "Contrat réactivé.", refresh })}>
                    Réactiver
                  </Button>
                )}
                <Button
                  variant="danger"
                  small
                  busy={a.busy}
                  onClick={async () => {
                    const r = await a.run(() => del(org.api(`/finance/supplier-contracts/${c.id}`)), { success: "Contrat supprimé.", refresh });
                    if (r) onClose();
                  }}
                >
                  Supprimer
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function ContractModal({ contract, onClose }: { contract: Contract | null; onClose: () => void }) {
  const org = useOrg();
  const refs = useRefs();
  const catalog = useCatalog();
  const c = contract;
  const [f, setF] = useState({
    kind: c?.kind ?? "assurance_pno",
    supplier: c?.supplier ?? "",
    label: c?.label ?? "",
    reference: c?.reference ?? "",
    property_id: c?.property_id ?? "",
    building_id: c?.building_id ?? "",
    start_on: c?.start_on ?? "",
    end_on: c?.end_on ?? "",
    tacit_renewal: c?.tacit_renewal ?? true,
    renewal_months: String(c?.renewal_months ?? 12),
    notice_days: String(c?.notice_days ?? 60),
    annual_cost_cents: c?.annual_cost_cents ?? (null as number | null),
    document_id: c?.document_id ?? "",
    document_name: c?.document_filename ?? "",
    clause_source: c?.clause_source ?? "",
    dates_confirmed: c?.dates_confirmed ?? true,
    notes: c?.notes ?? "",
  });
  const a = useAction();
  const up = useAction();
  const save = async () => {
    const missing = required([
      [!!f.supplier.trim(), "supplier", "Indiquez le fournisseur."],
      [!!f.start_on, "start_on", "Indiquez la date de début."],
    ]);
    if (missing) return a.setError(missing);
    const body = {
      ...f,
      property_id: f.property_id || null,
      building_id: f.property_id ? null : f.building_id || null,
      end_on: f.end_on || null,
      start_on: f.start_on || null,
      renewal_months: Number(f.renewal_months || 0),
      notice_days: Number(f.notice_days || 0),
      document_id: f.document_id || null,
    };
    const r = await a.run(() => (c ? put(org.api(`/finance/supplier-contracts/${c.id}`), body) : post(org.api("/finance/supplier-contracts"), body)), { success: "Contrat enregistré ; le rappel est à jour.", refresh: [org.api("/finance")] });
    if (r) onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={c ? "Modifier le contrat" : "Nouveau contrat"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>Fermer</Button>
          <Button busy={a.busy} onClick={save}>Enregistrer</Button>
        </>
      }
    >
      <div className="stack">
        <ErrorBox error={a.error && !(a.error as { fields?: unknown[] }).fields?.length ? a.error : null} />
        <div className="form-row">
          <Select label="Type de contrat" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={(catalog.data?.contract_kinds ?? []).map((k) => ({ value: k.code, label: k.label }))} />
          <Input label="Fournisseur" required value={f.supplier} error={fieldError(a.error, "supplier")} onChange={(e) => setF({ ...f, supplier: e.target.value })} />
        </div>
        <div className="form-row">
          <Input label="Libellé" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          <Input label="Numéro de contrat" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
        </div>
        <div className="form-row">
          <PropertySelect refs={refs.data} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} empty="Plusieurs lots ou tout le patrimoine" />
          {!f.property_id ? <BuildingSelect refs={refs.data} value={f.building_id} onChange={(v) => setF({ ...f, building_id: v })} empty="Aucun immeuble" /> : null}
        </div>
        <div className="form-row">
          <Input label="Date de début" type="date" required value={f.start_on} error={fieldError(a.error, "start_on")} onChange={(e) => setF({ ...f, start_on: e.target.value })} />
          <Input label="Échéance (fin de période)" type="date" value={f.end_on} error={fieldError(a.error, "end_on")} onChange={(e) => setF({ ...f, end_on: e.target.value })} />
          <Input label="Préavis (jours)" inputMode="numeric" value={f.notice_days} error={fieldError(a.error, "notice_days")} onChange={(e) => setF({ ...f, notice_days: e.target.value.replace(/\D/g, "") })} />
        </div>
        <div className="form-row">
          <Checkbox label="Reconduction tacite" checked={f.tacit_renewal} onChange={(v) => setF({ ...f, tacit_renewal: v })} />
          <Input label="Durée de reconduction (mois)" inputMode="numeric" value={f.renewal_months} error={fieldError(a.error, "renewal_months")} onChange={(e) => setF({ ...f, renewal_months: e.target.value.replace(/\D/g, "") })} />
          <MoneyInput label="Coût annuel" cents={f.annual_cost_cents} onChange={(v) => setF({ ...f, annual_cost_cents: v })} />
        </div>
        <div className="stack-sm">
          <p className="small"><strong>Contrat joint</strong> {f.document_id ? <DocLink id={f.document_id} name={f.document_name || "pièce jointe"} /> : "— aucun"}</p>
          <FileDrop
            busy={up.busy}
            label={f.document_id ? "Remplacer le contrat" : "Joindre le contrat"}
            onFiles={async (files) => {
              const form = new FormData();
              form.append("file", files[0]);
              form.append("category", "contrat_fournisseur");
              form.append("title", `Contrat ${f.supplier}`.trim());
              if (f.property_id) form.append("property_id", f.property_id);
              const doc = await up.run(() => upload<{ id: string; filename: string }>(org.api("/documents"), form), { success: "Contrat joint." });
              if (doc) setF({ ...f, document_id: doc.id, document_name: doc.filename });
            }}
          />
          <ErrorBox error={up.error} />
        </div>
        <Input label="Clause source de l’échéance et du préavis" placeholder="Ex. Conditions particulières, article 4, page 2" value={f.clause_source} error={fieldError(a.error, "clause_source")} onChange={(e) => setF({ ...f, clause_source: e.target.value })} />
        <Checkbox label="J’ai vérifié ces dates dans le contrat" help="Décochez si elles viennent d’une lecture automatique non encore confirmée." checked={f.dates_confirmed} onChange={(v) => setF({ ...f, dates_confirmed: v })} />
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
    </Modal>
  );
}
